import { Phone } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * Cleans a phone number for the dialler.
 */
function cleanNumber(phone) {
  if (!phone) return "";
  let num = phone.replace(/\s*(ext|x|#)\.?\s*\d*$/i, "").trim();
  let cleaned = num.replace(/[\s\-\(\)\.]/g, "");
  if (cleaned.startsWith("0") && !cleaned.startsWith("00")) {
    cleaned = "+44" + cleaned.substring(1);
  }
  if (cleaned.startsWith("00")) {
    cleaned = "+" + cleaned.substring(2);
  }
  if (!cleaned.startsWith("+") && cleaned.length >= 10) {
    cleaned = "+" + cleaned;
  }
  return cleaned;
}

/**
 * CallButton — initiates a Hub API call via the Dialler page.
 * Replaces the old tel: link approach. No 3CX client required.
 *
 * Props:
 *  - phone: string
 *  - label: optional string
 *  - compact: boolean
 *  - showNumber: boolean
 *  - contactName: optional string (pre-fills the contact name)
 */
export default function CallButton({ phone, label, compact = false, showNumber = true, contactName = "" }) {
  const navigate = useNavigate();

  if (!phone || !phone.trim()) return null;
  // Redacted numbers (masked by the backend) must not be callable.
  if (/[*•]/.test(phone)) return null;

  const display = phone;
  const cleaned = cleanNumber(phone);

  const handleCall = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const params = new URLSearchParams({ phone: cleaned, autoCall: "1" });
    if (contactName) params.set("name", contactName);
    navigate(`/hub/dialer?${params.toString()}`);
  };

  if (compact) {
    return (
      <button
        onClick={handleCall}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 transition-colors text-xs font-medium"
        data-testid="call-btn-compact"
        title={`Call ${display}`}
      >
        <Phone size={12} weight="bold" />
        {showNumber && <span>{display}</span>}
        {!showNumber && <span>Call</span>}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="call-btn-block">
      {label && <span className="text-[11px] text-[#6B7280] uppercase tracking-wide font-semibold w-[100px] flex-shrink-0">{label}</span>}
      <span className="text-sm text-[#0F172A] font-medium w-[160px] flex-shrink-0">{display}</span>
      <button
        onClick={handleCall}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-xs font-semibold shadow-sm"
        data-testid={`call-btn-${label?.toLowerCase().replace(/\s/g, "-") || "main"}`}
      >
        <Phone size={14} weight="bold" /> CALL
      </button>
    </div>
  );
}

/**
 * PhoneList — renders multiple phone numbers with call buttons.
 */
export function PhoneList({ phones }) {
  const validPhones = (phones || []).filter(p => p.number && p.number.trim());
  if (validPhones.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="phone-list">
      {validPhones.map((p, i) => (
        <CallButton key={i} phone={p.number} label={p.label} contactName={p.contactName} />
      ))}
    </div>
  );
}
